import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, FileX2, Plus } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { SimpleTable as Table, TableCell, TableHeader, TableRow } from '@components/Table';
import { companyService } from '@services/companyService';
import { invoiceInvalidationService } from '@services/invoiceInvalidationService';
import { Company, formatCnpj } from '@/types/company';
import {
  InvoiceInvalidation,
  INVOICE_INVALIDATION_STATUS_OPTIONS,
} from '@/types/invoiceInvalidation';
import { getInvoiceModelLabel } from '@/types/invoice';
import { getInvalidationStatusBadge } from './statusBadge';

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export const InvoiceInvalidationListView: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const canCreate = checkPermission('invoice_invalidation.create').hasPermission;

  const [items, setItems] = useState<InvoiceInvalidation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);

  const [companyId, setCompanyId] = useState<number | undefined>(undefined);
  const [statusId, setStatusId] = useState<number | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await invoiceInvalidationService.list({
        page: currentPage,
        pageSize,
        company_id: companyId,
        status_id: statusId,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar invalidações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    companyService.getCompanies().then(setCompanies).catch(() => {});
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    loadItems();
  };

  return (
    <PermissionGuard
      permission="invoice_invalidation.read"
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para visualizar invalidações de NF-e.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-h1">Invalidação de Notas</h1>
            <p className="text-slate-600">
              Submeta faixas de numeração de NF-e/NFC-e para inutilização junto à SEFAZ.
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => navigate('/settings/fiscal/invoice-invalidations/new')} icon={<Plus size={20} />}>
              Nova Invalidação
            </Button>
          )}
        </div>

        <Card>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Empresa</label>
                <select
                  className="input-field w-full text-sm px-3 py-2"
                  value={companyId ?? ''}
                  onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : undefined)}
                >
                  <option value="">Todas</option>
                  {companies.map((c) => (
                    <option key={c.company_id} value={c.company_id}>
                      {c.name} — {formatCnpj(c.document)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Status</label>
                <select
                  className="input-field w-full text-sm px-3 py-2"
                  value={statusId ?? ''}
                  onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : undefined)}
                >
                  <option value="">Todos</option>
                  {INVOICE_INVALIDATION_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">De</label>
                <DateInputBR
                  className="input-field w-full text-sm px-3 py-2"
                  value={dateFrom}
                  onChange={setDateFrom}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Até</label>
                <DateInputBR
                  className="input-field w-full text-sm px-3 py-2"
                  value={dateTo}
                  onChange={setDateTo}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleSearch} fullWidth>
                  Buscar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">ID</th>
                  <th className="text-left">Empresa</th>
                  <th className="text-left">Modelo</th>
                  <th className="text-left">Série</th>
                  <th className="text-left">Faixa</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Protocolo</th>
                  <th className="text-left">Data</th>
                  <th className="text-left">Usuário</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={10}>
                        <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-10 text-center text-slate-500">
                      <FileX2 className="mx-auto mb-2 text-slate-300" size={32} />
                      Nenhuma invalidação encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const badge = getInvalidationStatusBadge(item.status_id, item.status_name);
                    return (
                      <TableRow key={item.invalidation_id}>
                        <TableCell className="font-mono text-sm">{item.invalidation_id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="text-slate-800">{item.company_name || `Empresa #${item.company_id}`}</p>
                            {item.company_document && (
                              <p className="text-xs text-slate-500 font-mono">{formatCnpj(item.company_document)}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getInvoiceModelLabel(item.model)}</TableCell>
                        <TableCell className="font-mono text-sm">{item.serie}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {item.number_start} — {item.number_end}
                          <span className="ml-1 text-xs text-slate-400">
                            ({item.number_end - item.number_start + 1})
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                            {badge.label}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.protocol_number || '—'}</TableCell>
                        <TableCell>{formatDate(item.created_at)}</TableCell>
                        <TableCell className="text-xs text-slate-600">{item.created_by || '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/settings/fiscal/invoice-invalidations/${item.invalidation_id}`)}
                            icon={<Eye size={14} />}
                          >
                            Detalhe
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </tbody>
            </Table>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Anterior
              </Button>
              <span className="text-sm text-slate-600">
                Página {currentPage} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};
